import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

const emptyDrug = () => ({ name: '', dose: '', route: '' })

const CPRProtocol = () => {
  const settings = useContext(TenantSettingsContext).orgConfig?.orgSettings
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  const [cprCycleLengthSeconds, setCprCycleLengthSeconds] = useState(120)
  const [drugs, setDrugs] = useState([])

  useEffect(() => {
    setCprCycleLengthSeconds(
      settings?.cardiacArrestProtocol?.cprCycleLengthSeconds ?? 120,
    )
    setDrugs(settings?.cardiacArrestProtocol?.drugs ?? [])
  }, [settings])

  const updateDrug = (index, field, value) => {
    setDrugs((prev) =>
      prev.map((drug, i) => (i === index ? { ...drug, [field]: value } : drug)),
    )
  }

  const removeDrug = (index) => {
    setDrugs((prev) => prev.filter((_, i) => i !== index))
  }

  const addDrug = () => {
    setDrugs((prev) => [...prev, emptyDrug()])
  }

  const handleSaveChanges = (e) => {
    e.preventDefault()
    API.put('/site-config', {
      cardiacArrestProtocol: {
        cprCycleLengthSeconds: Number(cprCycleLengthSeconds) || 0,
        drugs,
      },
    }).then((res) => {
      if (res.status === 200) {
        setToast(true)
        setOrgConfig((prev) => ({
          ...prev,
          orgSettings: {
            ...prev.orgSettings,
            ...res.data,
          },
        }))
      }
    })
  }

  return (
    <Stack gap={3} component="form" onSubmit={handleSaveChanges}>
      <Stack gap={1}>
        <Typography variant="title2">Cardiac Arrest Protocol</Typography>
        <Alert severity="info">
          This sets a default timing template for the mobile cardiac arrest
          tool. It is not clinical guidance — protocol responsibility remains
          with your organisation.
        </Alert>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3" fontSize="1rem">
          CPR Cycle Length
        </Typography>
        <TextField
          type="number"
          value={cprCycleLengthSeconds}
          onChange={(e) => setCprCycleLengthSeconds(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ maxWidth: 220 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
          }}
        />
        <Typography variant="caption">
          The mobile cardiac arrest timer counts down from this length after
          each shock (default 120 seconds, matching a standard 2-minute CPR
          cycle).
        </Typography>
      </Stack>

      <Stack gap={1}>
        <Typography variant="title3" fontSize="1rem">
          Drug List
        </Typography>
        <Typography variant="caption">
          Shown as quick-select options when logging a drug during a cardiac
          arrest record.
        </Typography>
        <Stack gap={1}>
          {drugs.map((drug, index) => (
            <Stack key={index} direction="row" gap={1} alignItems="center">
              <TextField
                label="Name"
                value={drug.name}
                onChange={(e) => updateDrug(index, 'name', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Dose"
                value={drug.dose}
                onChange={(e) => updateDrug(index, 'dose', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Route"
                value={drug.route}
                onChange={(e) => updateDrug(index, 'route', e.target.value)}
                size="small"
                fullWidth
              />
              <IconButton onClick={() => removeDrug(index)} aria-label="Remove drug">
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          onClick={addDrug}
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Drug
        </Button>
      </Stack>

      <Button color="success" type="submit" variant="contained" size="small" fullWidth>
        Save Changes
      </Button>
    </Stack>
  )
}

export default CPRProtocol
